import {
  Button,
  Container,
  Group,
  Paper,
  Select,
  Stack,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCreateProject, type CreateProjectData, type ProjectType } from "@/hooks/useProjects";

const PROJECT_TYPE_OPTIONS: { value: ProjectType; label: string }[] = [
  { value: "residential_new", label: "Residential - New Construction" },
  { value: "renovation", label: "Renovation / Remodel" },
  { value: "commercial_buildout", label: "Commercial Build-out" },
  { value: "site_work", label: "Site Work" },
  { value: "multi_family", label: "Multi-Family" },
  { value: "custom", label: "Custom / Other" },
];

export const Route = createFileRoute("/projects/new")({
  component: NewProjectPage,
});

function NewProjectPage() {
  const navigate = useNavigate();
  const createProject = useCreateProject();

  const form = useForm<CreateProjectData>({
    initialValues: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      projectType: "residential_new" as ProjectType,
      targetEndDate: "",
    },
    validate: {
      name: (value) =>
        value.trim().length < 2 ? "Project name is required" : null,
    },
  });

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      const result = await createProject.mutateAsync(values);
      notifications.show({
        title: "Success",
        message: "Project created successfully!",
        color: "green",
      });
      navigate({ to: "/projects/$projectId", params: { projectId: result.project.id } });
    } catch (error) {
      notifications.show({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to create project",
        color: "red",
      });
    }
  });

  const handleCancel = () => {
    navigate({ to: "/projects" });
  };

  return (
    <Container size="sm" py="xl">
      <Paper p="xl" withBorder>
        <Stack gap="md">
          <Title order={2}>Create New Project</Title>

          <form onSubmit={handleSubmit}>
            <Stack gap="md">
              <TextInput
                label="Project Name"
                placeholder="Enter project name"
                {...form.getInputProps("name")}
                required
              />

              <Select
                label="Project Type"
                placeholder="Select project type"
                data={PROJECT_TYPE_OPTIONS}
                {...form.getInputProps("projectType")}
                allowDeselect={false}
              />

              <TextInput
                label="Target End Date"
                placeholder="Select target end date"
                type="date"
                {...form.getInputProps("targetEndDate")}
              />

              <TextInput
                label="Address"
                placeholder="Enter street address"
                {...form.getInputProps("address")}
              />

              <Group grow>
                <TextInput
                  label="City"
                  placeholder="City"
                  {...form.getInputProps("city")}
                />
                <TextInput
                  label="State"
                  placeholder="State"
                  {...form.getInputProps("state")}
                />
              </Group>

              <TextInput
                label="ZIP Code"
                placeholder="ZIP code"
                {...form.getInputProps("zip")}
              />

              <Group justify="flex-end" mt="md">
                <Button
                  variant="subtle"
                  onClick={handleCancel}
                  disabled={createProject.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createProject.isPending}
                >
                  Create Project
                </Button>
              </Group>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
